import type { ChangeEvent, MouseEvent as ReactMouseEvent } from 'react';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import styled from 'styled-components';
import type { DragMoveEvent } from '@dnd-kit/core';
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import type { TextStyleValue } from '@elfsight/configurator-contracts';
import { getColorWithAlpha } from '@elfsight/lib-utils';
import { createReadableClass } from '@elfsight/widget-utils';
import { clamp } from 'es-toolkit';
import { useRect } from 'react-use-rect';
import { commonFieldsSettings } from '../../commonFieldSettings';
import { SliderDraggableRail } from './slider-draggable-rail';

export type SliderComponentProps = {
  initialValue: number;
  step?: number;

  fieldActiveColor: string;
  fieldColor: string;
  fieldFontStyle?: TextStyleValue;

  onChange: (value: number, event?: ChangeEvent<HTMLInputElement>) => void;

  minValue: number;
  maxValue: number;
  numberFormatter: (value: number) => string;
  ariaLabel?: string;
};

export type SliderAPI = {
  setValue: (value: number | undefined) => void;
};

export const SliderComponent = forwardRef<SliderAPI, SliderComponentProps>(
  function SliderComponent(
    {
      initialValue = 0,
      fieldActiveColor = commonFieldsSettings.fieldActiveColor,
      fieldColor = commonFieldsSettings.fieldColor,
      onChange,
      maxValue = 0,
      minValue = 100,
      step,
      numberFormatter,
      ariaLabel
    },
    apiRef
  ) {
    const sensors = useSensors(
      useSensor(MouseSensor, {
        activationConstraint: {
          distance: 1
        }
      }),
      useSensor(TouchSensor)
    );

    const [bounds, setBounds] = useState<Omit<DOMRect, 'toJSON'> | undefined>();
    const [sliderRef] = useRect(setBounds, { resize: true });
    const currentValueRef = useRef(initialValue);

    useImperativeHandle(
      apiRef,
      () => ({
        setValue: (value: number | undefined) => {
          if (value !== undefined) {
            currentValueRef.current = value;
          }
        }
      }),
      []
    );

    const getNextValue = (x: number) => {
      const percent = (x - (bounds?.x || 0)) / (bounds?.width || 1);
      const nextValue = percentToValue(percent, minValue, maxValue);
      return step ? roundValueToStep(nextValue, step, minValue) : nextValue;
    };

    const handleDrag = (event: DragMoveEvent) => {
      let clientX = 0;
      if (event.activatorEvent.type === 'touchstart') {
        const activatorEvent = event.activatorEvent as TouchEvent;
        clientX = activatorEvent.touches[0].clientX;
      }
      if (event.activatorEvent.type === 'mousedown') {
        const activatorEvent = event.activatorEvent as MouseEvent;
        clientX = activatorEvent.clientX;
      }

      const nextValue = getNextValue(event.delta.x + clientX);

      currentValueRef.current = nextValue;
      onChange(nextValue);
    };

    const handleTrackClick = ({ clientX }: ReactMouseEvent) => {
      const nextValue = getNextValue(clientX);

      currentValueRef.current = nextValue;
      onChange(nextValue);
    };

    const percent = valueToPercent(currentValueRef.current, minValue, maxValue);

    return (
      <DndContext
        modifiers={[restrictToHorizontalAxis]}
        autoScroll={false}
        sensors={sensors}
        onDragMove={handleDrag}
      >
        <Container>
          <SliderDraggableRail
            ariaLabel={ariaLabel}
            ariaValueMin={minValue}
            ariaValueMax={maxValue}
            ariaValueNow={currentValueRef.current || initialValue}
            onClick={handleTrackClick}
            ref={sliderRef}
            color={getColorWithAlpha(fieldColor, 0.05)}
          >
            <SliderTrack
              _color={fieldActiveColor}
              style={{ width: `${percent}%` }}
            />
            <SliderThumb style={{ left: `${percent}%` }} />
          </SliderDraggableRail>
          <Labels _color={getColorWithAlpha(fieldColor, 0.7)}>
            <Label $align="left">{numberFormatter(minValue)}</Label>
            <Label $align="right">{numberFormatter(maxValue)}</Label>
          </Labels>
        </Container>
      </DndContext>
    );
  }
);

const Container = styled.div.attrs({
  className: createReadableClass({
    component: 'slider-component',
    element: 'container'
  })
})`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SliderTrack = styled.div.attrs<{ _color: string }>({
  className: createReadableClass({
    component: 'slider-component',
    element: 'track'
  })
})`
  position: absolute;
  top: 0;
  left: 0;
  width: 0;
  height: 100%;
  border-radius: 5px;
  background: ${({ _color }) => _color};
`;
const SliderThumb = styled.div.attrs({
  className: createReadableClass({
    component: 'slider-component',
    element: 'thumb'
  })
})`
  position: absolute;
  width: 20px;
  height: 20px;
  left: 50%;
  margin-top: -5px;
  margin-left: -10px;
  border-radius: 100%;
  border: 1px solid rgba(17, 17, 17, 0.05);
  background: #fff;
  box-shadow: 0 1px 6px 0 rgba(0, 0, 0, 0.3);
  z-index: 1;
  cursor: grab;

  &:before {
    content: '';
    left: -7px;
    top: -7px;
    width: 32px;
    height: 32px;
    position: absolute;
  }
`;

const Labels = styled.div.attrs<{ _color: string }>({
  className: createReadableClass({
    component: 'slider-component',
    element: 'labels'
  })
})`
  display: flex;
  gap: 16px;
  justify-content: space-between;
  color: ${({ _color }) => _color};
  font-size: 13px;
`;

const Label = styled.span.attrs<{ $align: string }>({
  className: createReadableClass({
    component: 'slider-component',
    element: 'label'
  })
})`
  text-align: ${({ $align }) => $align};
`;

function valueToPercent(value: number, min: number, max: number) {
  return ((value - min) * 100) / (max - min);
}

function percentToValue(percent: number, min: number, max: number) {
  return clamp((max - min) * percent + min, min, max);
}

function getDecimalPrecision(num: number) {
  if (Math.abs(num) < 1) {
    const parts = num.toExponential().split('e-');
    const decimalPart = parts[0].split('.')[1];
    return (decimalPart ? decimalPart.length : 0) + parseInt(parts[1], 10);
  }

  const decimalPart = num.toString().split('.')[1];
  return decimalPart ? decimalPart.length : 0;
}

function roundValueToStep(value: number, step: number, min: number) {
  const nearest = Math.round((value - min) / step) * step + min;
  return Number(nearest.toFixed(getDecimalPrecision(step)));
}
